/**
 * "Contrast Settings" modal for the SHSAT "Change the background and
 * foreground color" accessibility tool. Seven official TestNav themes as
 * radio options + a Continue button. Live-previews the theme as the student
 * changes the selection (the parent applies it immediately); Continue just
 * closes the modal. Cancel/backdrop reverts to the theme that was active
 * when the modal opened.
 *
 * Self-contained inline styling (no new CSS class file) that itself
 * consumes the theme variables via `var(--shsat-*, <default>)`, so the
 * modal recolors with the chosen theme and matches the default UI when
 * unthemed.
 */

import React from 'react';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from 'react-icons/io';

import { SHSAT_THEMES, ShsatThemeId } from './shsatTheme';

export function ContrastSettingsModal(props: {
  value: ShsatThemeId;
  onPreview: (id: ShsatThemeId) => void;
  onContinue: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { value, onPreview, onContinue, onCancel } = props;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Contrast Settings"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: '90vw',
          background: 'var(--shsat-surface, #ffffff)',
          color: 'var(--shsat-fg, #313131)',
          border: '1px solid var(--shsat-border, #bdbdbd)',
          borderRadius: 8,
          padding: '24px 28px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
        }}
      >
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>
          Contrast Settings
        </h2>
        <div style={{ margin: '0 0 16px', fontSize: 14, opacity: 0.85 }}>
          Change the background and foreground color.
        </div>
        <div role="radiogroup" aria-label="Contrast themes">
          {SHSAT_THEMES.map((theme) => {
            const selected = theme.id === value;
            return (
              <div
                key={theme.id}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => onPreview(theme.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(theme.id); }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 6px',
                  cursor: 'pointer',
                  fontWeight: selected ? 600 : 400,
                }}
              >
                {selected
                  ? <IoMdRadioButtonOn style={{ color: 'var(--shsat-accent, #2f80ed)', fontSize: '1.25em', flexShrink: 0 }} />
                  : <IoMdRadioButtonOff style={{ color: 'var(--shsat-glyph-muted, #676767)', fontSize: '1.25em', flexShrink: 0 }} />}
                {theme.label}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 18px',
              borderRadius: 5,
              border: '1px solid var(--shsat-border, #bdbdbd)',
              background: 'transparent',
              color: 'inherit',
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            style={{
              padding: '8px 22px',
              borderRadius: 5,
              border: '1px solid #005bb5',
              background: 'linear-gradient(to bottom, #1c75e8, #005bb5)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContrastSettingsModal;
